import React, { useState } from 'react';
import { Input, Label } from 'reactstrap';
import classnames from 'classnames';
import RequiredField from './RequiredField';
import Co2Popover from 'Components/Co2Common/TooltipPopover';
import Co2UniversalButton from './Co2UniversalButton';
import Co2CircleButton from './Co2CircleButton';

// Define Props Interface
interface Co2ButtonInputProps {
    name: string;
    label?: string;
    value: any;
    readOnly?: boolean;
    placeholder: string;
    invalid: boolean;
    error: string;
    loader: boolean;
    handleSearch: () => void;
    iconClass?: string;
    iconPosition?: 'left' | 'right';
    handleBlur(e: any): void;
    handleChange(e: any): void;
    className?: string;
    buttonLabel?: string;
    buttonTitle?: string;
    hideButton?: boolean;
    leftComponent?: React.ReactNode;
    required?: boolean;
    showHelp?: boolean;
    uppercase?: boolean;
    allowSearchWhenReadOnly?: boolean;
    autoFocus?: boolean;
    inputRef?: React.Ref<HTMLInputElement>;
}

const Co2ButtonInput: React.FC<Co2ButtonInputProps> = ({
    name,
    label,
    value,
    placeholder,
    invalid,
    error,
    readOnly,
    loader,
    handleSearch,
    allowSearchWhenReadOnly = false,
    handleBlur,
    handleChange,
    className,
    iconClass = 'fa fa-light fa-magnifying-glass',
    iconPosition = 'left',
    buttonLabel,
    buttonTitle = 'Search',
    hideButton = false,
    leftComponent,
    required = true,
    showHelp = true,
    uppercase = false,
    autoFocus = false,
    inputRef,
}) => {
    const [focused, setFocused] = useState<boolean>(false);

    const searchDisabled = loader || (readOnly && !allowSearchWhenReadOnly);
    const normalizedIconClass = iconClass.trim();
    const circleIconVariant = normalizedIconClass.includes('pen-to-square')
        ? 'edit'
        : normalizedIconClass.includes('plus')
            ? 'add'
            // 'other' keeps the given icon; branded variants would swap the X for their own icon
            : normalizedIconClass.includes('xmark')
                ? 'other'
                : 'search';
    // Only search-style actions (magnifier icon, or a text button on an editable field) fire on Enter;
    // add/edit icon actions open modals and must stay click-only.
    const searchOnEnter = !hideButton && !searchDisabled && (
        normalizedIconClass === '' ? !readOnly : circleIconVariant === 'search' && !readOnly
    );

    return (
        <div className={className ? className : ''}>
            {label && (
                <Label
                    htmlFor={name}
                    className={classnames({
                        'form-label': true,
                        'text-danger': invalid,
                        //"text-black": !invalid,
                    })}
                >
                    {invalid ? error : label}
                    {required && <RequiredField />}
                    {showHelp && <Co2Popover />}
                </Label>
            )}

            {/* Input Group */}
            <div className={iconClass != '' && !hideButton ? '' : 'hstack gap-1'}>
                <div
                    className={classnames(
                        'w-100 input-group form-control p-0 overflow-hidden',
                        {
                            'd-flex align-items-stretch': iconClass != '' && !hideButton,
                            'focused-style': focused,
                            'border-danger': invalid,
                            'form-control-readOnly': readOnly,
                        }
                    )}
                >
                    {leftComponent && leftComponent}
                    <Input
                        type="text"
                        autoFocus={autoFocus}
                        innerRef={inputRef}
                        className="border-0 flex-grow-1"
                        style={{
                            outline: 'none',
                            boxShadow: 'none',
                            border: '0px',
                            textTransform: uppercase ? 'uppercase' : undefined,
                            minWidth: 0,
                        }}
                        id={name}
                        placeholder={placeholder}
                        disabled={loader}
                        name={name}
                        readOnly={readOnly}
                        value={value}
                        onBlur={(e) => {
                            handleBlur(e);
                            setFocused(false);
                        }}
                        onChange={handleChange}
                        onKeyDown={(e) => {
                            if (e.key !== 'Enter' || e.nativeEvent.isComposing || !searchOnEnter) return;
                            // Enter runs the field's search instead of submitting the surrounding form
                            e.preventDefault();
                            handleSearch();
                        }}
                        onFocus={() => setFocused(true)}
                    />
                    {!hideButton && iconClass != '' && (
                        <div
                            className="co2-field-action-cell d-flex align-items-center justify-content-center"
                            style={{
                                width: 32,
                                flexShrink: 0,
                                alignSelf: 'stretch',
                                marginLeft: 0,
                                position: 'relative',
                                zIndex: 1,
                                boxShadow: `inset 1px 0 0 var(${readOnly ? '--co2-readonly-control-border' : '--vz-input-field-border'})`,
                            }}
                        >
                            <Co2CircleButton
                                onClick={handleSearch}
                                disabled={searchDisabled}
                                iconVariant={circleIconVariant}
                                iconClass={normalizedIconClass}
                                title={buttonTitle}
                            />
                        </div>
                    )}
                </div>
                {!hideButton && (
                    //dddd, we need to fix this Co2Button if no label is provided to be the small one
                    <>
                        {iconClass == '' && (
                            <Co2UniversalButton
                                onClick={handleSearch}
                            >
                                {buttonLabel}
                            </Co2UniversalButton>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Co2ButtonInput;
